import { SCHEDULE_BY_ID } from '../constants';

/**
 * O que o calendário diz de cada evento, e o que ele soma num período.
 *
 * Mora fora do DashboardTab porque são duas telas lendo a mesma regra: o
 * cartão da semana no quadro e a imagem que vai para o dono do laboratório.
 * Se cada uma contasse do seu jeito, a foto mandada no WhatsApp diria 55
 * transferidos e a tela diria outra coisa.
 */

const n = (v) => Number(v) || 0;

const pct = (parte, todo) => (todo > 0 ? `${Math.round((parte / todo) * 100)}%` : null);

const plural = (q, um, varios) => `${q} ${q === 1 ? um : varios}`;

/** Feito é a etapa marcada como concluída — a data sozinha não basta. */
const feita = (service, etapa) => !!service[SCHEDULE_BY_ID[etapa]?.doneField];

/**
 * A linha de detalhe de um evento do calendário.
 *
 * Duas perguntas diferentes conforme o evento já aconteceu ou não. Feito, ele
 * diz o RESULTADO (326 oócitos, 20 prenhes de 55). Agendado, ele diz o que vai
 * ser TRABALHADO — e esse número vem da etapa anterior, porque o da própria
 * etapa ainda é zero: o DG30 agendado checa as receptoras que a TE transferiu.
 *
 * Sem número a dizer, cai na observação do agendamento; sem ela, `null`, e o
 * cartão fica só com o nome. Um "0 oócitos" numa OPU que ainda não aconteceu
 * leria como OPU perdida.
 */
export const detalheDoEvento = (ev) => {
  const s = ev?.service || {};

  if (ev?.prazo) {
    const v = n(ev.prazo.valor);
    return v > 0
      ? { texto: `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, feito: false }
      : null;
  }

  const etapa = ev?.type === 'retirada' ? 'protocolo' : ev?.type;
  const cfg = SCHEDULE_BY_ID[etapa];
  if (!cfg) return null;

  // A retirada é o dia de tirar o implante, não o protocolo: ela nunca está
  // "feita" pelo protocolo estar feito, só repete quantas receptoras são.
  const ok = ev.type !== 'retirada' && feita(s, etapa);
  let texto = null;

  switch (etapa) {
    case 'protocolo':
      if (n(s.protocolo_quantity) > 0) {
        texto = ok
          ? `${plural(n(s.protocolo_quantity), 'receptora protocolada', 'receptoras protocoladas')}`
          : plural(n(s.protocolo_quantity), 'receptora', 'receptoras');
      }
      break;
    case 'opu':
      if (ok && n(s.opu_quantity) > 0) texto = plural(n(s.opu_quantity), 'oócito', 'oócitos');
      break;
    case 'fiv':
      if (ok && n(s.lab_quantity) > 0) texto = plural(n(s.lab_quantity), 'embrião', 'embriões');
      break;
    case 'te':
      if (ok && n(s.te_quantity) > 0) texto = `${n(s.te_quantity)} transferido${n(s.te_quantity) === 1 ? '' : 's'}`;
      else if (!ok && n(s.lab_quantity) > 0) {
        texto = `${plural(n(s.lab_quantity), 'embrião disponível', 'embriões disponíveis')}`;
      }
      break;
    case 'dg30':
      if (ok) {
        const taxa = pct(n(s.pregnancy_30d_quantity), n(s.te_quantity));
        texto = `${n(s.pregnancy_30d_quantity)} prenhe${n(s.pregnancy_30d_quantity) === 1 ? '' : 's'}`
          + (n(s.te_quantity) > 0 ? ` de ${n(s.te_quantity)}` : '')
          + (taxa ? ` (${taxa})` : '');
      } else if (n(s.te_quantity) > 0) {
        texto = plural(n(s.te_quantity), 'receptora', 'receptoras');
      }
      break;
    case 'dg60':
      if (ok) {
        texto = `${n(s.pregnancy_60d_quantity)}`
          + (n(s.pregnancy_30d_quantity) > 0 ? ` de ${n(s.pregnancy_30d_quantity)}` : '')
          + ` confirmada${n(s.pregnancy_60d_quantity) === 1 ? '' : 's'}`;
      } else if (n(s.pregnancy_30d_quantity) > 0) {
        texto = `${n(s.pregnancy_30d_quantity)} prenhe${n(s.pregnancy_30d_quantity) === 1 ? '' : 's'} a confirmar`;
      }
      break;
    default:
      break;
  }

  if (!texto && !ok && cfg.notesField && s[cfg.notesField]) {
    texto = String(s[cfg.notesField]).trim() || null;
  }
  return texto || ok ? { texto, feito: ok } : null;
};

// ---------------------------------------------------------------------------

/** A data em que a etapa aconteceu: a realizada, e a agendada só se faltar. */
const dataFeita = (service, etapa) => {
  const cfg = SCHEDULE_BY_ID[etapa];
  if (!feita(service, etapa)) return null;
  return String(service[cfg.actualField] || service[cfg.field] || '').slice(0, 10) || null;
};

const LINHA_VAZIA = () => ({
  protocolos: 0, receptoras: 0,
  opus: 0, oocitos: 0,
  fivs: 0, embrioes: 0,
  tes: 0, transferidos: 0,
  dg30: 0, prenhes: 0, checadas: 0,
  dg60: 0, confirmadas: 0, reconfirmar: 0,
});

/**
 * O que foi FEITO entre duas datas, inclusive as duas.
 *
 * Conta etapa concluída cuja data realizada cai no período — a agendada não
 * conta, porque a pergunta é o que aconteceu. Cada etapa entra no período em
 * que ela mesma aconteceu: a OPU de um serviço pode estar num mês e a TE dele
 * no outro, e cada mês fica com a sua parte.
 *
 * A taxa de prenhez é das prenhes sobre as TRANSFERIDAS DAQUELES serviços, e
 * não sobre as TEs do período: um DG30 de outubro olha TEs de setembro.
 */
export const resumoDoPeriodo = (services = [], de, ate) => {
  const dentro = (d) => !!d && d >= de && d <= ate;
  const total = LINHA_VAZIA();
  const porCliente = new Map();

  const somar = (service, fn) => {
    const chave = service.client_id || service.client_name || '—';
    if (!porCliente.has(chave)) {
      porCliente.set(chave, { cliente: service.client_name || '—', ...LINHA_VAZIA() });
    }
    fn(total);
    fn(porCliente.get(chave));
  };

  services.forEach(s => {
    if (dentro(dataFeita(s, 'protocolo'))) {
      somar(s, l => { l.protocolos += 1; l.receptoras += n(s.protocolo_quantity); });
    }
    if (dentro(dataFeita(s, 'opu'))) {
      somar(s, l => { l.opus += 1; l.oocitos += n(s.opu_quantity); });
    }
    if (dentro(dataFeita(s, 'fiv'))) {
      somar(s, l => { l.fivs += 1; l.embrioes += n(s.lab_quantity); });
    }
    if (dentro(dataFeita(s, 'te'))) {
      somar(s, l => { l.tes += 1; l.transferidos += n(s.te_quantity); });
    }
    if (dentro(dataFeita(s, 'dg30'))) {
      somar(s, l => { l.dg30 += 1; l.prenhes += n(s.pregnancy_30d_quantity); l.checadas += n(s.te_quantity); });
    }
    if (dentro(dataFeita(s, 'dg60'))) {
      somar(s, l => {
        l.dg60 += 1;
        l.confirmadas += n(s.pregnancy_60d_quantity);
        l.reconfirmar += n(s.pregnancy_30d_quantity);
      });
    }
  });

  const clientes = [...porCliente.values()].sort((a, b) => a.cliente.localeCompare(b.cliente, 'pt-BR'));
  return {
    de, ate, total, clientes,
    taxaDG30: pct(total.prenhes, total.checadas),
    taxaDG60: pct(total.confirmadas, total.reconfirmar),
    vazio: clientes.length === 0,
  };
};

// ---------------------------------------------------------------------------

const nomeDoMes = (iso, longo) => new Date(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, 1))
  .toLocaleDateString('pt-BR', { month: longo ? 'long' : 'short', timeZone: 'UTC' })
  .replace('.', '');

/**
 * O periodo escrito como quem fala, nao como quem formata.
 *
 * "23 a 29 de setembro de 2026" e nao "23/09 a 29/09": o mes e o ano so
 * aparecem uma vez quando sao os mesmos, e aparecem duas quando o periodo
 * atravessa a virada — que e exatamente quando alguem precisa reparar.
 */
export const periodoPorExtenso = (ini, fim) => {
  const d = (iso) => Number(iso.slice(8, 10));
  if (ini === fim) return `${d(ini)} de ${nomeDoMes(ini, true)} de ${ini.slice(0, 4)}`;
  if (ini.slice(0, 7) === fim.slice(0, 7)) {
    return `${d(ini)} a ${d(fim)} de ${nomeDoMes(ini, true)} de ${ini.slice(0, 4)}`;
  }
  if (ini.slice(0, 4) === fim.slice(0, 4)) {
    return `${d(ini)} de ${nomeDoMes(ini, false)} a ${d(fim)} de ${nomeDoMes(fim, false)} de ${fim.slice(0, 4)}`;
  }
  return `${d(ini)} de ${nomeDoMes(ini, false)} de ${ini.slice(0, 4)}`
    + ` a ${d(fim)} de ${nomeDoMes(fim, false)} de ${fim.slice(0, 4)}`;
};
